import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { fmtIDR, todayISO } from "@/lib/format";
import { Download, Wallet, TrendingUp, TrendingDown, Building } from "lucide-react";

const ArusKas = () => {
  const [start, setStart] = useState(new Date().toISOString().slice(0, 7) + "-01");
  const [end, setEnd] = useState(todayISO());
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/reports/arus-kas", { params: { start, end } }).then(r => setData(r.data));
  }, [start, end]);

  const download = () => {
    const token = localStorage.getItem("spbu_token");
    fetch(`${API}/export/arus-kas.xlsx?start=${start}&end=${end}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob()).then(b => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a"); a.href = url; a.download = "arus_kas.xlsx"; a.click();
      });
  };

  if (!data) return <div className="text-slate-500">Memuat...</div>;

  const sections = [
    {
      title: "Arus Kas Aktivitas Operasi", icon: TrendingUp, color: "emerald",
      rows: [
        ["Kas Masuk dari Penjualan", data.operating.cash_in_sales],
        ["Kas Keluar untuk Pembelian BBM", -data.operating.cash_out_purchases],
        ["Kas Keluar Beban Operasional", -data.operating.cash_out_expenses],
      ],
      net: data.operating.net,
    },
    {
      title: "Arus Kas Aktivitas Investasi", icon: Building, color: "blue",
      rows: [["Perolehan Aset Tetap", data.investing.asset_acquisition]],
      net: data.investing.net,
    },
    {
      title: "Arus Kas Aktivitas Pendanaan", icon: TrendingDown, color: "slate",
      rows: [["(Tidak ada aktivitas pendanaan)", 0]],
      net: data.financing.net,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Laporan Arus Kas</h1>
          <p className="text-sm text-slate-500 mt-1">Cash Flow Statement</p>
        </div>
        <div className="flex items-end gap-2">
          <div><Label className="text-xs">Dari</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} /></div>
          <div><Label className="text-xs">Sampai</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} /></div>
          <Button onClick={download} className="bg-[#005DAA] hover:bg-[#004885]"><Download className="w-4 h-4 mr-2" />Excel</Button>
        </div>
      </div>

      <Card className="bg-gradient-to-r from-[#0B0F17] to-[#111827] text-white border-0">
        <CardContent className="p-6 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Saldo Kas Akhir Periode</div>
            <div className="text-4xl font-extrabold font-mono-fig mt-2">{fmtIDR(data.ending_cash)}</div>
          </div>
          <div className="w-14 h-14 rounded-xl bg-white/10 grid place-items-center">
            <Wallet className="w-7 h-7 text-white" />
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {sections.map((s) => (
          <Card key={s.title}>
            <CardHeader>
              <CardTitle className="text-base font-bold flex items-center gap-2"><s.icon className={`w-4 h-4 text-${s.color}-600`} />{s.title}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {s.rows.map(([l, v], i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span className="text-slate-600">{l}</span>
                  <span className={`font-mono-fig ${v < 0 ? "text-rose-700" : v > 0 ? "text-emerald-700" : "text-slate-500"}`}>{fmtIDR(v)}</span>
                </div>
              ))}
              <div className="pt-2 border-t flex justify-between font-bold">
                <span>Net</span>
                <span className={`font-mono-fig ${s.net < 0 ? "text-rose-700" : "text-emerald-700"}`}>{fmtIDR(s.net)}</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default ArusKas;
